// Funciones puras del Portal de seguimiento: normalización, SLA, avance,
// colores por área, parseo de fechas, lectura de Excel y exportación.

import {
  AREA_COLORS,
  PALETTE,
  AVANCE_ESTATUS,
  DIA,
} from "./portal.constants";

export const pad = (n) => String(n).padStart(2, "0");

// ISO (yyyy-mm-dd) → "05 jun"; vacío → guion largo.
export const fmt = (iso) =>
  iso
    ? new Date(iso + "T00:00:00").toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
      })
    : "—";

// Normaliza texto: minúsculas, sin acentos, espacios colapsados.
export const normTxt = (v) =>
  String(v ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export function normEstatus(e) {
  const s = normTxt(e);
  if (!s) return "En proceso";
  if (s.includes("conclu")) return "Concluido";
  if (s.includes("stand")) return "Stand by";
  if (s.includes("declin")) return "Declinado";
  if (s.includes("proceso")) return "En proceso";
  return "En proceso";
}

export function normPrioridad(p) {
  const s = normTxt(p);
  if (s.startsWith("alt")) return "Alta";
  if (s.startsWith("baj")) return "Baja";
  if (s.startsWith("med")) return "Medio";
  return "Medio";
}

export const avanceDe = (r) =>
  r.avance != null ? r.avance : AVANCE_ESTATUS[normEstatus(r.estatus)] ?? 0;

// Estado de cumplimiento (SLA) respecto a la fecha compromiso y "ahora".
export function slaDe(r, now) {
  const est = normEstatus(r.estatus);
  const comp = r.fechaCompromiso
    ? new Date(r.fechaCompromiso + "T00:00:00")
    : null;
  if (est === "Concluido") {
    if (comp && r.fechaFinal)
      return new Date(r.fechaFinal + "T00:00:00") <= comp ? "A tiempo" : "Tarde";
    return "Concluido";
  }
  if (est === "Declinado") return "Sin fecha";
  if (!comp) return "Sin fecha";
  const diff = comp.getTime() - now.getTime();
  if (diff < 0) return "Vencido";
  if (diff < 3 * DIA) return "Por vencer";
  return "A tiempo";
}

// Rango [inicio, fin] usado por el cronograma (Gantt).
export function rangoDe(r) {
  const start = new Date(
    (r.fechaInicial ||
      r.fechaCompromiso ||
      new Date().toISOString().slice(0, 10)) + "T00:00:00"
  );
  let end = r.fechaFinal
    ? new Date(r.fechaFinal + "T00:00:00")
    : r.fechaCompromiso
    ? new Date(r.fechaCompromiso + "T00:00:00")
    : normEstatus(r.estatus) === "Concluido"
    ? start
    : new Date();
  if (end < start) end = start;
  return { start, end };
}

// Identidad estable de un registro (para dedupe en importaciones).
export const idDe = (r) =>
  `${normTxt(r.cliente)}|${normTxt(r.asunto).slice(0, 60)}|${
    r.fechaInicial || ""
  }`;

export function colorArea(a) {
  if (AREA_COLORS[a]) return AREA_COLORS[a];
  let h = 0;
  for (const ch of String(a || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

// Color de área con transparencia (fondo suave).
export const softArea = (a) => colorArea(a) + "22";

export function parseFecha(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date && !isNaN(v))
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) {
    const y = m[3].length === 2 ? "20" + m[3] : m[3];
    return `${y}-${pad(m[2])}-${pad(m[1])}`;
  }
  const d = new Date(s);
  return isNaN(d)
    ? null
    : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function parseAvance(v) {
  if (v == null || v === "") return null;
  const m = String(v).match(/(\d+)/);
  return m ? Math.min(100, parseInt(m[1])) : null;
}

// Forma "plana" de un registro para exportar / enviar al backend.
export function toRegistro(c) {
  return {
    folio: c.folio,
    cliente: c.cliente,
    solicitante: c.solicitante,
    solicitanteTipo: c.solicitanteTipo,
    canal: c.canal,
    categoria: c.categoria,
    asunto: c.asunto,
    prioridad: c.prioridad,
    areaResponsable: c.area,
    fechaInicial: c.fechaInicial,
    fechaCompromiso: c.fechaCompromiso,
    fechaFinal: c.fechaFinal,
    estatus: c.estatus,
    avance: avanceDe(c),
    comentarios: c.comentarios || "",
  };
}

function descargar(text, filename, type) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportJSON(casos) {
  const data = {
    _formato: "onest-cx-bridge",
    _version: 2,
    generado: new Date().toISOString(),
    registros: casos.map(toRegistro),
  };
  descargar(
    JSON.stringify(data, null, 2),
    "onest-cx-bridge.json",
    "application/json"
  );
}

export function exportCSV(casos) {
  const cols = [
    "folio", "cliente", "solicitante", "canal", "categoria", "asunto",
    "prioridad", "areaResponsable", "fechaInicial", "fechaCompromiso",
    "fechaFinal", "estatus", "avance",
  ];
  const cell = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const rows = casos.map(toRegistro);
  const csv = [cols.join(",")]
    .concat(rows.map((r) => cols.map((k) => cell(r[k])).join(",")))
    .join("\n");
  descargar(csv, "onest-matriz-incidencias.csv", "text/csv");
}

// Lee un Excel de "Matriz de incidencias / requerimientos" (hojas Claims y
// Proyectos CX) y devuelve registros normalizados. Usa SheetJS (xlsx).
export async function leerMatrizExcel(file) {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const out = [];
  wb.SheetNames.forEach((name) => {
    const ws = wb.Sheets[name];
    if (!ws) return;
    const aoa = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      raw: false,
      dateNF: "yyyy-mm-dd",
    });
    let hr = -1;
    for (let i = 0; i < Math.min(aoa.length, 15); i++) {
      const h = (aoa[i] || []).map(normTxt);
      if (
        h.some((x) => x.includes("estatus")) &&
        h.some(
          (x) =>
            x.includes("cliente") ||
            x.includes("nombre del proyecto") ||
            x.includes("area responsable")
        )
      ) {
        hr = i;
        break;
      }
    }
    if (hr < 0) return;
    const H = (aoa[hr] || []).map(normTxt);
    const col = (...al) => H.findIndex((h) => al.some((a) => h.includes(a)));
    const idx = {
      cliente: col("cliente"),
      tipo: col("ext o int"),
      solicitante: col("nombre del solicitante"),
      ini: col("fecha inicial"),
      canal: col("canal"),
      cat: col("categoria"),
      asunto: col("asunto"),
      prio: col("prioridad"),
      area: col("area responsable"),
      comp: col("fecha compromiso", "fechas compromiso"),
      fin: col("fecha final"),
      est: col("estatus"),
      av: col("% avance", "avance"),
      com: col("comentarios"),
      proy: col("nombre del proyecto"),
    };
    const g = (row, i) => (i >= 0 && row[i] != null ? String(row[i]).trim() : "");
    const esProyecto = idx.proy >= 0 && idx.asunto < 0;
    for (let r = hr + 1; r < aoa.length; r++) {
      const row = aoa[r] || [];
      const asunto = esProyecto ? g(row, idx.proy) : g(row, idx.asunto);
      const cliente = g(row, idx.cliente);
      if (!asunto && !cliente) continue;
      out.push({
        cliente: cliente || (esProyecto ? "Proyecto CX" : ""),
        solicitanteTipo: g(row, idx.tipo),
        solicitante: g(row, idx.solicitante),
        fechaInicial: parseFecha(idx.ini >= 0 ? row[idx.ini] : null),
        canal: g(row, idx.canal),
        categoria: esProyecto ? "Proyecto" : g(row, idx.cat) || "Requerimiento",
        asunto,
        prioridad: normPrioridad(g(row, idx.prio)),
        area: g(row, idx.area) || "Otros",
        fechaCompromiso: parseFecha(idx.comp >= 0 ? row[idx.comp] : null),
        fechaFinal: parseFecha(idx.fin >= 0 ? row[idx.fin] : null),
        estatus: normEstatus(g(row, idx.est)),
        avance: parseAvance(g(row, idx.av)),
        comentarios: g(row, idx.com),
        origen: name,
      });
    }
  });
  return out;
}
